import React, { useState } from 'react';
import { DocumentIcon, ArrowDownTrayIcon, TrashIcon, DocumentTextIcon, FolderIcon } from '@heroicons/react/24/outline';
import { formatFileSize } from '../../utils/formatters';
import { formatAttachmentUrl } from '../../utils/imageUtils';
import { Dialog, DialogContent } from '../ui/custom/Dialog';
import { TicketAttachment } from '../../types/ticket';
import { useTheme } from '../../contexts/ThemeContext';
import { attachmentService } from '../../services/attachmentService';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure pdf.js worker source
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

interface AttachmentPreviewProps {
  attachments: TicketAttachment[];
  onDelete?: (attachmentId: number) => Promise<void>;
  showDelete?: boolean;
}

// Component to render PDF thumbnails in the grid view
function PdfThumbnail({ filePath, theme }: { filePath: string; theme: string }) {
  const [thumbnail, setThumbnail] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  console.log("From pdfThumbnail", filePath)
  return (
    <div className={`relative w-full h-full flex items-center justify-center ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
      {!error ? (
        <>
          <Document
            file={{url: filePath}}
            onLoadSuccess={() => setThumbnail(true)}
            onLoadError={() => setError(true)}
            loading={null}
            className="pdf-thumbnail"
          >
            <Page
              pageNumber={1}
              width={120}
              height={120}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className={thumbnail ? '' : 'hidden'}
              scale={1.0}
            />
          </Document>
          {!thumbnail && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-red-400 rounded-full animate-spin mb-2"></div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Loading PDF</span>
            </div>
          )}
        </>
      ) : (
        <DocumentTextIcon className="h-12 w-12 text-red-400" />
      )}
      <div className="absolute bottom-0 right-0 bg-red-500 text-white text-[10px] px-1 rounded-tl-sm">
        PDF
      </div>
    </div>
  );
}

export default function AttachmentPreview({ attachments, onDelete, showDelete = false }: AttachmentPreviewProps) {
  const [selectedAttachment, setSelectedAttachment] = useState<TicketAttachment | null>(null);
  const [isDeleting, setIsDeleting] = useState<Record<number, boolean>>({});
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfError, setPdfError] = useState<boolean>(false);
  const [pdfLoading, setPdfLoading] = useState<boolean>(true);
  const [scale, setScale] = useState<number>(1.0);
  const { theme } = useTheme();

  const isImage = (fileType: string | null) => {
    return fileType?.startsWith('image/') || false;
  };

  const isPdf = (fileType: string | null, fileName?: string | null) => {
    // Check by MIME type
    if (fileType === 'application/pdf') return true;
    
    // Fallback: check by file extension if MIME type is missing or incorrect
    if (fileName && fileName.toLowerCase().endsWith('.pdf')) return true;
    
    return false;
  };

  const isZip = (fileType: string | null, fileName?: string | null) => {
    // Check by MIME type
    if (fileType === 'application/zip') return true;
    
    // Fallback: check by file extension if MIME type is missing or incorrect
    if (fileName && fileName.toLowerCase().endsWith('.zip')) return true;
    
    return false;
  };
  
  const getFileType = (fileType: string | null, fileName?: string | null): string => {
    // First check common MIME types
    if (fileType?.startsWith('image/')) return 'IMAGE';
    if (fileType === 'application/pdf' || (fileName && fileName.toLowerCase().endsWith('.pdf'))) return 'PDF';
    if (fileType === 'application/msword' || fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        (fileName && (fileName.toLowerCase().endsWith('.doc') || fileName.toLowerCase().endsWith('.docx')))) return 'DOC';
    if (fileType === 'application/vnd.ms-excel' || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        (fileName && (fileName.toLowerCase().endsWith('.xls') || fileName.toLowerCase().endsWith('.xlsx')))) return 'XLS';
    if (fileType === 'text/csv' || fileType === 'application/csv' ||
        (fileName && fileName.toLowerCase().endsWith('.csv'))) return 'CSV';
    if (fileType === 'text/plain' ||
        (fileName && fileName.toLowerCase().endsWith('.txt'))) return 'TXT';
    if(fileType === 'application/zip' || (fileName && fileName.toLowerCase().endsWith('.zip'))) return 'ZIP';
    
    // Default to generic document
    return 'DOC';
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(false);
  };

  const onDocumentLoadError = () => {
    setPdfError(true);
    setPdfLoading(false);
  };

  // Reset function for PDF viewer state
  const resetPdfState = () => {
    setPageNumber(1);
    setPdfLoading(true);
    setPdfError(false);
    setScale(1.0);
  };
  
  // Reset PDF state when selecting a new attachment
  React.useEffect(() => {
    if (selectedAttachment) {
      resetPdfState();
    }
  }, [selectedAttachment]);

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.6));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  const handleDownload = async (attachment: TicketAttachment) => {
    if (!attachment?.file_path) return;
    const match = attachment.file_path.match(/https:\/\/[^\/]+\.s3\.[^.]+\.amazonaws\.com\/(.*)/);
    console.log("match", match);
    if (match && match[1]) {
      // Add Preview URL to attachment
      attachmentService.downloadAttachment(attachment.id, attachment.file_name);
    } else {
      const link = document.createElement('a');
      link.href = formatAttachmentUrl(attachment.file_path);
      link.target = '_blank';
      link.download = attachment.file_name || '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDelete = async (e: React.MouseEvent, attachmentId: number) => {
    e.stopPropagation(); // Prevent opening the preview
    if (!onDelete) return;

    setIsDeleting(prev => ({ ...prev, [attachmentId]: true }));
    try {
      await onDelete(attachmentId);
    } finally {
      setIsDeleting(prev => ({ ...prev, [attachmentId]: false }));
    }
  };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className={`relative group cursor-pointer border rounded-lg p-2 ${
              theme === 'dark'
                ? 'border-gray-700 hover:bg-gray-800/50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setSelectedAttachment(attachment)}
          >
            {isImage(attachment.file_type) ? (
              <div className={`aspect-square w-full overflow-hidden rounded-md ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
              }`}>
                <img
                  src={attachment.preview_url || formatAttachmentUrl(attachment.file_path)}
                  alt={attachment.file_name || ''}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : isPdf(attachment.file_type, attachment.file_name) ? (
              <div className={`aspect-square w-full overflow-hidden rounded-md`}>
                <PdfThumbnail filePath={attachment.preview_url || formatAttachmentUrl(attachment.file_path)} theme={theme} />
              </div>
            ) : isZip(attachment.file_type, attachment.file_name) ? (
              <div className={`aspect-square w-full flex items-center justify-center rounded-md ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
              }`}>
                <FolderIcon className="h-12 w-12 text-gray-400" />
              </div>
            ) : (
              <div className={`aspect-square w-full flex items-center justify-center rounded-md ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
              }`}>
                <DocumentIcon className="h-12 w-12 text-gray-400" />
              </div>
            )}
            <div className={`mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              <p className="text-sm truncate" title={attachment.file_name || ''}>
                {attachment.file_name || ''}
              </p>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {formatFileSize(attachment.file_size || 0)}
                </p>
                {(() => {
                  const fileType = getFileType(attachment.file_type, attachment.file_name);
                  if (fileType !== 'IMAGE') {
                    const badgeClasses = {
                      PDF: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
                      DOC: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
                      XLS: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
                      CSV: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
                      TXT: "bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-400",
                      ZIP: "bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-400"
                    };
                    return (
                      <span className={`text-xs px-1.5 py-0.5 rounded-sm ${badgeClasses[fileType as keyof typeof badgeClasses] || badgeClasses.DOC}`}>
                        {fileType}
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Action buttons */}
            <div className={`absolute top-2 right-2 flex gap-2 ${
              theme === 'dark' ? 'bg-gray-800/90' : 'bg-white/90'
            } p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity`}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(attachment);
                }}
                className={`p-1 rounded-md ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-300'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                title="Download"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
              {showDelete && onDelete && (
                <button
                  onClick={(e) => handleDelete(e, attachment.id)}
                  disabled={isDeleting[attachment.id]}
                  className={`p-1 rounded-md ${
                    theme === 'dark'
                      ? 'hover:bg-red-900/50 text-red-400 hover:text-red-300'
                      : 'hover:bg-red-50 text-red-500 hover:text-red-700'
                  } ${isDeleting[attachment.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!selectedAttachment} onOpenChange={() => setSelectedAttachment(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          {selectedAttachment && (
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-4rem)]">
              {isImage(selectedAttachment.file_type) ? (
                <div className="flex items-center justify-center">
                  <img
                    src={selectedAttachment.preview_url || formatAttachmentUrl(selectedAttachment.file_path)}
                    alt={selectedAttachment.file_name || ''}
                    className="max-w-full max-h-[calc(90vh-8rem)] object-contain"
                  />
                </div>
              ) : isPdf(selectedAttachment.file_type, selectedAttachment.file_name) ? (
                <div className="flex flex-col items-center justify-center">
                  <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow-lg w-full max-w-2xl`}>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {selectedAttachment.file_name || 'PDF Document'}
                        </h3>
                        <span className="text-xs px-1.5 py-0.5 rounded-sm bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          PDF
                        </span>
                      </div>
                      <button
                        onClick={() => selectedAttachment && handleDownload(selectedAttachment)}
                        className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-50 hover:bg-blue-100 text-blue-700'}`}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Download
                      </button>
                    </div>
                    
                    {/* PDF Viewer */}
                    <div className="pdf-container border rounded-lg overflow-hidden flex justify-center items-center" style={{ minHeight: '60vh' }}>
                      {pdfLoading && (
                        <div className="flex flex-col items-center justify-center h-60">
                          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-2"></div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Loading PDF...</p>
                        </div>
                      )}
                      
                      {pdfError && (
                        <div className="flex flex-col items-center justify-center h-60">
                          <div className={`rounded-full h-10 w-10 flex items-center justify-center mb-2 ${theme === 'dark' ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-500'}`}>
                            <span className="text-xl">!</span>
                          </div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>Failed to load PDF</p>
                        </div>
                      )}

                      <Document
                        file={selectedAttachment.preview_url || formatAttachmentUrl(selectedAttachment.file_path)}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={null}
                        className="pdf-document"
                      >
                        <Page
                          pageNumber={pageNumber}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className={`${pdfLoading ? 'hidden' : ''}`}
                          width={Math.min(window.innerWidth * 0.8, 800) * scale}
                          scale={scale}
                        />
                      </Document>
                    </div>

                    {/* PDF Controls */}
                    {numPages && numPages > 0 && (
                      <div className="mt-4">
                        {/* Page Navigation */}
                        <div className="flex items-center justify-between mb-2">
                          <button
                            onClick={goToPrevPage}
                            disabled={pageNumber <= 1}
                            className={`px-3 py-1 rounded-md text-sm ${pageNumber <= 1 ? 'opacity-50 cursor-not-allowed' : ''} ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
                          >
                            Previous
                          </button>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Page {pageNumber} of {numPages}
                          </p>
                          <button
                            onClick={goToNextPage}
                            disabled={pageNumber >= (numPages || 1)}
                            className={`px-3 py-1 rounded-md text-sm ${pageNumber >= (numPages || 1) ? 'opacity-50 cursor-not-allowed' : ''} ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
                          >
                            Next
                          </button>
                        </div>
                        
                        {/* Zoom Controls */}
                        <div className="flex items-center justify-center gap-2 mt-2">
                          <button
                            onClick={zoomOut}
                            disabled={scale <= 0.6}
                            className={`p-1.5 rounded-md ${scale <= 0.6 ? 'opacity-50 cursor-not-allowed' : ''} ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            title="Zoom Out"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
                            </svg>
                          </button>
                          
                          <button
                            onClick={resetZoom}
                            className={`px-2 py-1 text-xs rounded-md ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            title="Reset Zoom"
                          >
                            {Math.round(scale * 100)}%
                          </button>
                          
                          <button
                            onClick={zoomIn}
                            disabled={scale >= 3.0}
                            className={`p-1.5 rounded-md ${scale >= 3.0 ? 'opacity-50 cursor-not-allowed' : ''} ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            title="Zoom In"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <DocumentIcon className="h-24 w-24 mx-auto text-gray-400" />
                  <p className="mt-2 text-lg font-medium">{selectedAttachment.file_name || ''}</p>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(selectedAttachment.file_size || 0)}
                  </p>
                  <button
                    onClick={() => selectedAttachment && handleDownload(selectedAttachment)}
                    className="mt-4 inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                    Download
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
